/**
 * insertIndexFromSnapshot — pure adjacency util for ordering a late-arriving
 * item against the working `messages` array, using the SDK's `history_updated`
 * snapshot as the source of truth for order.
 *
 * Background (voice-call transcript misorder bug): during a call, the user's
 * transcript is produced by a side-channel transcriber that can complete
 * AFTER the realtime model has already started streaming its reply — so a
 * naive "append on arrival" insert lands the question below its own answer.
 * The snapshot the SDK sends via `history_updated` carries the WHOLE
 * conversation in authoritative order (including the user's voice item, once
 * its transcript is done), so an item's position in the CONVERSATION can
 * always be derived from where it sits in the snapshot — independent of
 * which frame happened to deliver it, or when.
 *
 * Rule: an item belongs immediately before the first of its snapshot
 * successors that already exists in `messages`. Concretely — collect every
 * id that follows `itemId` in `snapshotIds`, then scan `messages` in order
 * and return the index of the first message whose id is in that successor
 * set. This is robust to rows in `messages` that aren't part of the history
 * snapshot at all (tool cards, system notices): they're simply never in the
 * successor set, so they're skipped over rather than tripping the search —
 * the item inserts as LATE as possible while still landing before its true
 * successor.
 *
 * No match (itemId absent from the snapshot, or it has no successors, or
 * none of its successors are in `messages` yet) → append (return
 * `messages.length`), which is also correct for an empty `messages` array.
 */
export function insertIndexFromSnapshot<T extends { id: string }>(
  messages: T[],
  snapshotIds: string[],
  itemId: string
): number {
  const position = snapshotIds.indexOf(itemId);
  if (position === -1) return messages.length;

  const successors = new Set(snapshotIds.slice(position + 1));
  if (successors.size === 0) return messages.length;

  for (let i = 0; i < messages.length; i++) {
    if (successors.has(messages[i].id)) return i;
  }
  return messages.length;
}
